/**
 * Web Push subscription management.
 *
 * The page's role is only to obtain a subscription from the browser and hand it
 * to the backend. Delivery is done by the browser vendor's push service, and the
 * resulting `push` event is handled in sw.js — which is why notifications arrive
 * with the app closed.
 */

import { registerServiceWorker } from "@/features/notifications/notifications";
import { apiPostJson } from "@/api/apiClient";

const VAPID_KEY_ENDPOINT = "push_vapid_public_key";
const SUBSCRIBE_ENDPOINT = "push_subscribe";
const UNSUBSCRIBE_ENDPOINT = "push_unsubscribe";
const SEND_TEST_ENDPOINT = "push_send_test";

export interface SendResult {
  sent: number;
  failed: number;
  pruned: number;
}

export function pushSupported(): boolean {
  return "PushManager" in window;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  const registration = await registerServiceWorker();
  return registration.pushManager.getSubscription();
}

/**
 * Subscribe this browser to push and register it with the backend.
 *
 * Safe to call repeatedly: an existing browser subscription is reused and simply
 * re-registered, which also repairs the case where the browser still holds a
 * subscription the backend has forgotten.
 */
export async function subscribeToPush(): Promise<PushSubscription> {
  const registration = await registerServiceWorker();

  let subscription = await registration.pushManager.getSubscription();
  if (subscription === null) {
    const key = await apiPostJson<{ public_key: string }>(VAPID_KEY_ENDPOINT);
    subscription = await registration.pushManager.subscribe({
      // Mandatory in Chrome, and every push must produce a visible
      // notification or the subscription gets revoked.
      userVisibleOnly: true,
      applicationServerKey: key.public_key,
    });
  }

  await apiPostJson<null>(SUBSCRIBE_ENDPOINT, subscription.toJSON());
  return subscription;
}

export async function unsubscribeFromPush(): Promise<void> {
  const subscription = await currentSubscription();
  if (subscription === null) return;

  // Tell the backend first: if the local unsubscribe succeeded and this call
  // then failed, the server would keep pushing to a dead endpoint until it 410s.
  await apiPostJson<null>(UNSUBSCRIBE_ENDPOINT, subscription.toJSON());
  await subscription.unsubscribe();
}

export async function sendTestPush(): Promise<SendResult> {
  return apiPostJson<SendResult>(SEND_TEST_ENDPOINT);
}
